import { useMemo } from 'react';

import type {
  SelectionList,
  SelectionListKey,
  SelectionListState,
} from '../../../../../shared/selection-lists/types';
import type { NestSelectionLists } from '../../../filters/selectionList/nestSelectionLists';
import {
  type Filter,
  FilterId,
  FilterType,
  type ListFilter,
  type TreeFilter,
} from '../../../filters/types/filters';
import type { CompositionSecondaryDimensionValues } from '../../PageConfigProvider/types/dimension';

export const useFilteredSelectionTree = ({
  secondaryDimension,
  effectiveFilters,
  selectionLists,
  usesStateBreakdownOverMsa = false,
}: {
  secondaryDimension: CompositionSecondaryDimensionValues;
  effectiveFilters: Filter[];
  selectionLists: SelectionListState | null;
  usesStateBreakdownOverMsa?: boolean;
}) => {
  return useMemo((): NestSelectionLists => {
    if (!selectionLists) return [];

    if (secondaryDimension === 'geography') {
      const geographyFilter = getTreeFilter({
        filters: effectiveFilters,
        filterId: FilterId.GEOGRAPHY,
      });
      if (!geographyFilter) {
        return [
          {
            selectionListId: 'region',
            selectionList: selectionLists.region ?? [],
          },
        ];
      }

      if (checkFilterLevelId({ filter: geographyFilter, levelId: 'region' })) {
        return [
          {
            selectionListId: 'region',
            selectionList: filterSelectionLists({
              selectionLists,
              selectionListId: 'region',
              filter: geographyFilter,
            }),
          },
          {
            selectionListId: 'country',
            selectionList: selectionLists.country ?? [],
          },
        ];
      }
      if (checkFilterLevelId({ filter: geographyFilter, levelId: 'country' })) {
        const nextLevelId = usesStateBreakdownOverMsa ? 'state' : 'msa';
        return [
          {
            selectionListId: 'country',
            selectionList: filterSelectionLists({
              selectionLists,
              selectionListId: 'country',
              filter: geographyFilter,
            }),
          },
          {
            selectionListId: nextLevelId,
            selectionList: selectionLists[nextLevelId] ?? [],
          },
        ];
      }

      // If city is selected (deepest) show only the selected cities
      if (checkFilterLevelId({ filter: geographyFilter, levelId: 'city' })) {
        return [
          {
            selectionListId: 'city',
            selectionList: filterSelectionLists({
              selectionLists,
              selectionListId: 'city',
              filter: geographyFilter,
            }),
          },
        ];
      }

      if (checkFilterLevelId({ filter: geographyFilter, levelId: 'state' })) {
        return [
          {
            selectionListId: 'state',
            selectionList: filterSelectionLists({
              selectionLists,
              selectionListId: 'state',
              filter: geographyFilter,
            }),
          },
          {
            selectionListId: 'city',
            selectionList: selectionLists.city ?? [],
          },
        ];
      }

      const defaultGeoId = usesStateBreakdownOverMsa ? 'state' : 'msa';
      return [
        {
          selectionListId: defaultGeoId,
          selectionList: filterSelectionLists({
            selectionLists,
            selectionListId: defaultGeoId,
            filter: geographyFilter,
          }),
        },
        {
          selectionListId: 'city',
          selectionList: selectionLists.city ?? [],
        },
      ];
    }

    if (secondaryDimension === 'role') {
      const roleFilter = getTreeFilter({
        filters: effectiveFilters,
        filterId: FilterId.ROLE,
      });
      if (!roleFilter) {
        return [
          {
            selectionListId: 'role_k7',
            selectionList: selectionLists.role_k7 ?? [],
          },
        ];
      }

      if (checkFilterLevelId({ filter: roleFilter, levelId: 'role_k7' })) {
        return [
          {
            selectionListId: 'role_k7',
            selectionList: filterSelectionLists({
              selectionLists,
              selectionListId: 'role_k7',
              filter: roleFilter,
            }),
          },
          {
            selectionListId: 'role_k50',
            selectionList: selectionLists.role_k50 ?? [],
          },
        ];
      }

      if (checkFilterLevelId({ filter: roleFilter, levelId: 'role_k150' })) {
        return [
          {
            selectionListId: 'role_k150',
            selectionList: filterSelectionLists({
              selectionLists,
              selectionListId: 'role_k150',
              filter: roleFilter,
            }),
          },
          {
            selectionListId: 'role_k500',
            selectionList: selectionLists.role_k500 ?? [],
          },
        ];
      }

      if (checkFilterLevelId({ filter: roleFilter, levelId: 'role_k1500' })) {
        return [
          {
            selectionListId: 'role_k1500',
            selectionList: filterSelectionLists({
              selectionLists,
              selectionListId: 'role_k1500',
              filter: roleFilter,
            }),
          },
          {
            selectionListId: 'role_k5000',
            selectionList: selectionLists.role_k5000 ?? [],
          },
        ];
      }

      return [
        {
          selectionListId: 'role_k15000',
          selectionList: filterSelectionLists({
            selectionLists,
            selectionListId: 'role_k15000',
            filter: roleFilter,
          }),
        },
      ];
    }

    if (secondaryDimension === 'skills') {
      const skillFilter = getTreeFilter({
        filters: effectiveFilters,
        filterId: FilterId.SKILL,
      });
      if (!skillFilter) {
        return [
          {
            selectionListId: 'skill_k75',
            selectionList: selectionLists.skill_k75 ?? [],
          },
        ];
      }

      if (checkFilterLevelId({ filter: skillFilter, levelId: 'skill_k75' })) {
        return [
          {
            selectionListId: 'skill_k75',
            selectionList: filterSelectionLists({
              selectionLists,
              selectionListId: 'skill_k75',
              filter: skillFilter,
            }),
          },
          {
            selectionListId: 'skill_k700',
            selectionList: selectionLists.skill_k700 ?? [],
          },
        ];
      }
      if (checkFilterLevelId({ filter: skillFilter, levelId: 'skill_k700' })) {
        return [
          {
            selectionListId: 'skill_k700',
            selectionList: filterSelectionLists({
              selectionLists,
              selectionListId: 'skill_k700',
              filter: skillFilter,
            }),
          },
          {
            selectionListId: 'skill_k3000',
            selectionList: selectionLists.skill_k3000 ?? [],
          },
        ];
      }
      return [
        {
          selectionListId: 'skill_k3000',
          selectionList: filterSelectionLists({
            selectionLists,
            selectionListId: 'skill_k3000',
            filter: skillFilter,
          }),
        },
      ];
    }

    if (secondaryDimension === 'rics') {
      const ricsFilter = getTreeFilter({
        filters: effectiveFilters,
        filterId: FilterId.COMPANY,
      });
      if (!ricsFilter) {
        return [
          {
            selectionListId: 'rics_k10',
            selectionList: selectionLists.rics_k10 ?? [],
          },
        ];
      }

      if (checkFilterLevelId({ filter: ricsFilter, levelId: 'rics_k10' })) {
        return [
          {
            selectionListId: 'rics_k10',
            selectionList: filterSelectionLists({
              selectionLists,
              selectionListId: 'rics_k10',
              filter: ricsFilter,
            }),
          },
          {
            selectionListId: 'rics_k400',
            selectionList: selectionLists.rics_k400 ?? [],
          },
        ];
      }
      return [
        {
          selectionListId: 'rics_k400',
          selectionList: filterSelectionLists({
            selectionLists,
            selectionListId: 'rics_k400',
            filter: ricsFilter,
          }),
        },
      ];
    }

    if (secondaryDimension === 'gender') {
      return getListSelectionList({
        selectionLists,
        filters: effectiveFilters,
        selectionListId: 'gender',
        filterId: FilterId.GENDER,
      });
    }

    if (secondaryDimension === 'ethnicity') {
      return getListSelectionList({
        selectionLists,
        filters: effectiveFilters,
        selectionListId: 'ethnicity',
        filterId: FilterId.ETHNICITY,
      });
    }

    if (secondaryDimension === 'education') {
      return getListSelectionList({
        selectionLists,
        filters: effectiveFilters,
        selectionListId: 'highest_degree',
        filterId: FilterId.EDUCATION,
      });
    }

    if (secondaryDimension === 'seniority') {
      return getListSelectionList({
        selectionLists,
        filters: effectiveFilters,
        selectionListId: 'seniority',
        filterId: FilterId.SENIORITY,
      });
    }

    return [];
  }, [
    selectionLists,
    effectiveFilters,
    secondaryDimension,
    usesStateBreakdownOverMsa,
  ]);
};
const getListSelectionList = ({
  selectionLists,
  filters,
  selectionListId,
  filterId,
}: {
  selectionLists: SelectionListState | null;
  filters: Filter[];
  selectionListId: SelectionListKey;
  filterId: FilterId;
}): NestSelectionLists => {
  const listItems = selectionLists?.[selectionListId];
  if (!listItems || listItems.length === 0) {
    return [];
  }

  const listFilter = getListFilter({ filters, filterId });
  if (!listFilter) {
    return [
      {
        selectionListId,
        selectionList: listItems,
      },
    ];
  }

  const selectedIds = new Set(listFilter.selections.map((sel) => sel.id));
  const filteredList = listItems.filter((item) =>
    item?.id ? selectedIds.has(item.id) : false
  );

  return [
    {
      selectionListId,
      selectionList: filteredList,
    },
  ];
};

const filterSelectionLists = <T extends SelectionListKey>({
  selectionLists,
  selectionListId,
  filter,
}: {
  selectionLists: SelectionListState<T>;
  selectionListId: T;
  filter: TreeFilter;
}): SelectionList<T> => {
  const currentSelectionIds = filter.selections
    .filter((selection) => selection?.levelId === selectionListId)
    .map((selection) => selection?.id?.split('_')?.slice(-1)?.[0]);

  const filteredSelectionList = selectionLists?.[selectionListId]?.filter(
    (item) => currentSelectionIds.includes(item?.id ?? '')
  );

  return filteredSelectionList ?? [];
};

const getTreeFilter = ({
  filters,
  filterId,
}: {
  filters: Filter[];
  filterId: FilterId;
}): TreeFilter | null => {
  const filter = filters.find((filter) => filter.id === filterId);

  if (!filter || filter.type !== FilterType.TREE) return null;

  return filter;
};

const getListFilter = ({
  filters,
  filterId,
}: {
  filters: Filter[];
  filterId: FilterId;
}): ListFilter | null => {
  const filter = filters.find((filter) => filter.id === filterId);

  if (!filter || filter.type !== FilterType.LIST) return null;

  return filter;
};

const checkFilterLevelId = ({
  filter,
  levelId,
}: {
  filter: TreeFilter;
  levelId: SelectionListKey;
}) => {
  return filter.selections.some((selection) => selection?.levelId === levelId);
};
